import {
  collection,
  doc,
  increment,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  writeBatch,
  arrayUnion,
  serverTimestamp,
  updateDoc,
  type CollectionReference,
  type FirestoreError,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { shortDocumentConverter } from "@kekkle/shared/convertors/web/short-document.converter";
import type { ShortDocument } from "@kekkle/shared/types/documents/short-document/short-document";
import type { ReportType } from "@/types/firestore/report";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import { setToast } from "../notifications/toast";

/**
 * Return the shorts collection reference with converter
 */
export function shortsCollection(
  groupId: string,
): CollectionReference<ShortDocument> {
  return collection(firestore, `groups/${groupId}/shorts`).withConverter(
    shortDocumentConverter,
  );
}

import type { SnapshotMetadata } from "@/types/firestore/metadata";
export type { SnapshotMetadata };

/**
 * Subscribes to all shorts for a group, ordered by timestamp descending.
 * Uses onSnapshot for real-time updates and leverages persistentLocalCache
 * to serve results instantly from cache on subsequent visits.
 */
export function subscribeToShorts(
  groupId: string,
  callbacks: {
    onShorts: (shorts: ShortDocument[], metadata: SnapshotMetadata) => void;
    onError: (error: FirestoreError) => void;
  },
): () => void {
  const q = query(shortsCollection(groupId), orderBy("timestamp", "desc"));
  return onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snapshot) => {
      callbacks.onShorts(
        snapshot.docs.map((doc) => doc.data()),
        {
          fromCache: snapshot.metadata.fromCache,
          hasPendingWrites: snapshot.metadata.hasPendingWrites,
        },
      );
    },
    (error) => {
      callbacks.onError(error);
    },
  );
}

/**
 * Adds a new short to the group
 */
export async function addShort(
  groupId: string,
  data: Omit<ShortDocument, "_id">,
): Promise<string> {
  try {
    const col = shortsCollection(groupId);
    const newShortRef = doc(col);
    await setDoc(newShortRef, data as ShortDocument);
    return newShortRef.id;
  } catch (e) {
    console.error(e);
    setToast("warning", { message: JSON.stringify(e) });
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Deletes a short from the group
 */
export async function deleteShort(
  groupId: string,
  shortId: string,
): Promise<void> {
  try {
    const batch = writeBatch(firestore);
    const col = shortsCollection(groupId);
    const shortRef = doc(col, shortId);
    const groupCollection = collection(firestore, "groups/");
    const groupRef = doc(groupCollection, groupId);

    batch.delete(shortRef);
    batch.update(groupRef, {
      "general_data.number_of_shorts": increment(-1),
    });

    await batch.commit();
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Reports a short and creates a report document
 */
export async function reportShort(
  groupId: string,
  shortId: string,
  reporterId: string,
): Promise<void> {
  try {
    const batch = writeBatch(firestore);
    const col = shortsCollection(groupId);
    const shortRef = doc(col, shortId);

    // Update short with reporter UUID
    batch.update(shortRef, {
      reported_by: arrayUnion(reporterId),
    });

    // Create report document
    const reportsCollection = collection(firestore, "reports");
    const reportRef = doc(reportsCollection);
    batch.set(reportRef, {
      group_id: groupId,
      type: "short" as ReportType,
      document_id: shortId,
      created_at: serverTimestamp(),
      created_by: reporterId,
      solved: false,
      solved_at: null,
    });

    await batch.commit();
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Marks a short as viewed by appending the user's UUID to viewed_by array
 */
export async function markShortAsViewed(
  groupId: string,
  shortId: string,
  userId: string,
): Promise<void> {
  try {
    const col = shortsCollection(groupId);
    const shortRef = doc(col, shortId);
    await updateDoc(shortRef, {
      viewed_by: arrayUnion(userId),
      updated_at: serverTimestamp(),
    });
  } catch (e) {
    console.error("Failed to mark short as viewed:", e);
  }
}
